import { useEffect, useMemo, useState } from 'react'
import { Grid3x3, RefreshCw } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Technique {
  id: string
  name: string
  description: string
  actor_count: number
  actors: string[]
}

interface Tactic {
  id: string
  label: string
  techniques: Technique[]
  technique_count: number
}

interface Matrix {
  tactics: Tactic[]
  total_techniques: number
  total_actors: number
  max_actor_count: number
  generated_at?: string
  stale?: boolean
}

/** Tactics × techniques heatmap — one column per kill-chain phase. Cell colour
 * intensity = how many actors in our catalog use this technique. Click a cell
 * to see the actor list. */
export default function AttackMatrixPage() {
  const [data, setData] = useState<Matrix | null>(null)
  const [loading, setLoading] = useState(true)
  const [picked, setPicked] = useState<Technique | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  useEffect(() => {
    api.get('/api/cyber/attack-matrix')
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }, [])

  const triggerRefresh = async () => {
    setRefreshing(true)
    try {
      await api.post('/api/cyber/internal/refresh-attack-matrix')
      const r = await api.get('/api/cyber/attack-matrix')
      setData(r.data)
    } catch {} finally { setRefreshing(false) }
  }

  const max = data?.max_actor_count || 1

  const heat = (n: number) => {
    if (n === 0) return 'bg-surface-raised text-text-muted border-border'
    const intensity = Math.min(1, n / max)
    if (intensity > 0.66) return 'bg-rose/40 text-rose-light border-rose/60'
    if (intensity > 0.33) return 'bg-orange/30 text-orange border-orange/50'
    if (intensity > 0.10) return 'bg-amber/20 text-amber-light border-amber/40'
    return 'bg-blue/15 text-blue-light border-blue/30'
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Grid3x3 size={24} className="text-rose" /> ATT&CK Matrix
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            {data?.total_techniques || 0} MITRE ATT&CK techniques · heatmap by actor coverage from our 969-actor catalog
          </p>
        </div>
        <button type="button" onClick={triggerRefresh} className="btn-secondary text-xs">
          <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} /> Refresh matrix
        </button>
      </div>

      {loading && <div className="card p-6 text-center text-text-muted text-sm">Loading matrix…</div>}
      {!loading && (!data || data.stale) && (
        <div className="card p-6 text-center text-text-muted text-sm">
          Matrix not built yet. Click "Refresh matrix" — the daily Celery task will keep it warm after that.
        </div>
      )}

      {data && !data.stale && (
        <div className="overflow-x-auto">
          <div className="grid auto-cols-[160px] grid-flow-col gap-2 pb-3" style={{ minWidth: 'max-content' }}>
            {data.tactics.filter(t => t.technique_count > 0).map(tactic => (
              <div key={tactic.id} className="flex flex-col gap-1">
                <div className="sticky top-0 bg-surface-raised border-b border-border px-2 py-1.5 z-10">
                  <div className="text-[11px] font-semibold text-text-primary truncate">{tactic.label}</div>
                  <div className="text-[9px] text-text-muted">{tactic.technique_count} techniques</div>
                </div>
                {tactic.techniques.map(t => (
                  <button key={t.id} type="button" onClick={() => setPicked(t)}
                    className={clsx('text-left p-1.5 rounded border text-[10px] hover:scale-[1.02] transition-transform',
                      heat(t.actor_count))}>
                    <div className="font-mono font-semibold">{t.id}</div>
                    <div className="line-clamp-2 leading-tight">{t.name}</div>
                    {t.actor_count > 0 && (
                      <div className="text-[9px] mt-1 opacity-80">{t.actor_count} actor{t.actor_count === 1 ? '' : 's'}</div>
                    )}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {picked && (
        <div className="fixed inset-0 z-[600] bg-black/70 backdrop-blur flex items-center justify-center p-4"
          onClick={() => setPicked(null)}>
          <div className="card max-w-xl w-full p-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-bold text-text-primary">
                <span className="font-mono text-rose">{picked.id}</span> · {picked.name}
              </h2>
              <button type="button" onClick={() => setPicked(null)} className="text-text-muted hover:text-text-primary">×</button>
            </div>
            <p className="text-[11px] text-text-secondary mb-3">{picked.description}</p>
            <div className="text-[11px] font-semibold text-text-primary mb-1">
              {picked.actor_count} attributed actor{picked.actor_count === 1 ? '' : 's'}
            </div>
            <div className="flex flex-wrap gap-1">
              {picked.actors.map(a => (
                <span key={a} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-surface-raised border border-border">
                  {a}
                </span>
              ))}
              {picked.actors.length === 0 && <span className="text-[10px] text-text-muted italic">None mapped in our catalog yet.</span>}
            </div>
            <a href={`https://attack.mitre.org/techniques/${picked.id}/`}
              target="_blank" rel="noreferrer noopener"
              className="text-[10px] text-blue-light hover:underline mt-3 inline-block">
              Open on attack.mitre.org →
            </a>
          </div>
        </div>
      )}
    </div>
  )
}
